import {
  CfarContractStatus,
  type FlightItineraryRequest,
  type FlightItineraryResponse,
  FlightItineraryState,
  ResponseEnum,
  type SingleFlightItineraryRequest,
  type SingleFlightItineraryResponse,
} from "@b2bportal/air-booking-api";
import type { AppDispatch } from "@b2bportal/core-types";
import { flightsBookingApi } from "@overrides/utilities";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";

import { FlightPostBookingActions } from "./slice";

export const ALL_FLIGHT_STATES = [
  FlightItineraryState.Canceled,
  FlightItineraryState.Future,
  FlightItineraryState.Present,
  FlightItineraryState.Past,
];

export const flightsPostBookingApi = createApi({
  reducerPath: "flightsPostBooking",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["FlightItineraryList"],
  endpoints: (builder) => ({
    fetchAllFlightItineraries: builder.query<
      FlightItineraryResponse,
      FlightItineraryRequest
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        queryArgs.states.slice().sort().join(","),
      queryFn: async (request) => {
        try {
          const response =
            await flightsBookingApi.apiV0ItineraryFlightPut(request);
          const data = response.data;
          if (data.Response === ResponseEnum.Success) {
            const value = data.value;
            const future = value.itineraries.future ?? [];
            const exercised = future.filter(
              (itinerary) =>
                itinerary.cfar?.status === CfarContractStatus.Exercised
            );
            if (exercised.length > 0) {
              value.itineraries.future = future.filter(
                (itinerary) =>
                  itinerary.cfar?.status !== CfarContractStatus.Exercised
              );
              value.itineraries.canceled = [
                ...(value.itineraries.canceled ?? []),
                ...exercised,
              ];
            }
            return { data: value };
          }
          return { error: { error: String(data) } };
        } catch (error) {
          return { error: { error: String(error) } };
        }
      },
      providesTags: ["FlightItineraryList"],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            FlightPostBookingActions.updateContext({
              airlines: data.context.airlines,
              airports: data.context.airports,
            })
          );
        } catch (e) {
          console.error("[flightsPostBookingApi] Failed to update context:", e);
        }
      },
    }),
    fetchFlightItinerary: builder.query<
      SingleFlightItineraryResponse,
      SingleFlightItineraryRequest
    >({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.itineraryId,
      queryFn: async (request) => {
        try {
          const response =
            await flightsBookingApi.apiV0ItineraryFlightSinglePut(request);
          return { data: response.data };
        } catch (error) {
          return { error: { error: String(error) } };
        }
      },
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            FlightPostBookingActions.updateContext({
              airlines: data.context.airlines,
              airports: data.context.airports,
            })
          );
        } catch (e) {
          console.error("[flightsPostBookingApi] Failed to update context:", e);
        }
      },
    }),
  }),
});

// Wrapper that synchronously sets selectedItineraryId before calling initiate().
// onQueryStarted does not fire for cache hits, so we dispatch the ID here where
// it's guaranteed to run regardless of cache state.
export const fetchFlightItineraryAndSelect =
  (args: SingleFlightItineraryRequest) => (dispatch: AppDispatch) => {
    dispatch(FlightPostBookingActions.setSelectedItineraryId(args.itineraryId));
    return dispatch(
      flightsPostBookingApi.endpoints.fetchFlightItinerary.initiate(args)
    );
  };
